import { ShieldCheckIcon, ScaleIcon, AcademicCapIcon } from '@heroicons/react/24/outline';
import { useTranslation } from 'react-i18next';
import type { RuleCategoryKey } from '../../types/policy.types';

interface CategoryBadgeProps {
  category: RuleCategoryKey;
  size?: 'xs' | 'sm' | 'md';
  showIcon?: boolean;
  className?: string;
}

const CATEGORY_TOKENS: Record<
  RuleCategoryKey,
  { bg: string; text: string; ring: string; icon: React.ElementType }
> = {
  compliance: {
    bg: 'bg-indigo-100',
    text: 'text-indigo-800',
    ring: 'ring-indigo-200',
    icon: ShieldCheckIcon,
  },
  legal_risk: {
    bg: 'bg-red-100',
    text: 'text-red-800',
    ring: 'ring-red-200',
    icon: ScaleIcon,
  },
  qa: {
    bg: 'bg-blue-100',
    text: 'text-blue-800',
    ring: 'ring-blue-200',
    icon: AcademicCapIcon,
  },
};

const SIZE_TOKENS: Record<
  NonNullable<CategoryBadgeProps['size']>,
  { padding: string; text: string; iconSize: string }
> = {
  xs: { padding: 'px-1.5 py-0.5', text: 'text-[10px]', iconSize: 'h-3 w-3' },
  sm: { padding: 'px-2 py-0.5', text: 'text-xs', iconSize: 'h-3.5 w-3.5' },
  md: { padding: 'px-2.5 py-1', text: 'text-sm', iconSize: 'h-4 w-4' },
};

export default function CategoryBadge({
  category,
  size = 'sm',
  showIcon = true,
  className = '',
}: CategoryBadgeProps) {
  const { t } = useTranslation('policy');
  const tokens = CATEGORY_TOKENS[category];
  const sizing = SIZE_TOKENS[size];
  const Icon = tokens.icon;
  const label = t(`categories.${category}.label`, {
    defaultValue:
      category === 'legal_risk'
        ? 'Legal Risk'
        : category === 'compliance'
        ? 'Compliance'
        : 'QA',
  });
  return (
    <span
      className={`inline-flex items-center gap-1 rounded-full font-medium ring-1 ring-inset ${tokens.bg} ${tokens.text} ${tokens.ring} ${sizing.padding} ${sizing.text} ${className}`}
    >
      {showIcon && <Icon className={sizing.iconSize} aria-hidden="true" />}
      {label}
    </span>
  );
}
